import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import api from '../../api/api';
import layers from './mapLayer';

function setupMap(containerId, mapStyle, center, zoom) {
    mapboxgl.accessToken = process.env.VUE_APP_MAPBOX_TOKEN;
    const map = new mapboxgl.Map({
        container: containerId,
        style: mapStyle,
        center: center,
        zoom: zoom,
    });
    return map;
}

function createClickEvents(map) {
    map.on('click', 'data-clientAddress', (e) => {
        new mapboxgl.Popup()
            .setLngLat(e.lngLat)
            .setHTML("Area Name:  " + e.features[0].properties.areaDescription + '<br>' +
            "Number Of Client(s): " + e.features[0].properties.numberOfClient + '<br>')
            .addTo(map);
    });

    map.on('click', 'data-smoking', (e) => {
        let regularSmoker = e.features[0].properties.regularSmoker;
        let exSmoker = e.features[0].properties.exSmoker;
        let neverSmoked = e.features[0].properties.neverSmokeRegularly;
        if (typeof regularSmoker === 'number') {
            regularSmoker = (regularSmoker  * 100).toFixed(2) + '%';
            exSmoker = (exSmoker  * 100).toFixed(2) + '%';
            neverSmoked = (neverSmoked  * 100).toFixed(2) + '%';
        }
        new mapboxgl.Popup()
            .setLngLat(e.lngLat)
            .setHTML("Area Name:  " + e.features[0].properties.areaDescription + '<br>' +
            "Number Of Client(s): " + e.features[0].properties.numberOfClient + '<br>' +
            "Regular Smoker: " + regularSmoker + '<br>' +
                "Ex Smoker: " + exSmoker + '<br>' +
                "Never Smoker Regularly: " + neverSmoked)
            .addTo(map);
    });

    map.on('click', 'data-maori-descent', (e) => {
        let maoriDescent = e.features[0].properties.maoriDescent;
        if (typeof maoriDescent === 'number') {
            maoriDescent = (maoriDescent * 100).toFixed(2) + '%';
        }
        new mapboxgl.Popup()
            .setLngLat(e.lngLat)
            .setHTML("Area Name:  " + e.features[0].properties.areaDescription + '<br>' +
            "Number Of Client(s): " + e.features[0].properties.numberOfClient + '<br>' +
            "Māori Population in the Area (%): " + maoriDescent)
            .addTo(map);
    });

    map.on('click', 'data-social-deprivation', (e) => {
        new mapboxgl.Popup()
        .setLngLat(e.lngLat)
        .setHTML("Area Name:  " + e.features[0].properties.areaDescription + '<br>' +
        "Number Of Client(s): " + e.features[0].properties.numberOfClient + '<br>' +
        "Social Deprivation: " + parseInt(e.features[0].properties.socialDeprivation * 100) + ' %')
        .addTo(map);
    });

    map.on('click', 'data-ethnicity', (e) => {
        new mapboxgl.Popup()
        .setLngLat(e.lngLat)
    .setHTML("Area Name:  " + e.features[0].properties.areaDescription + '<br>' +
    "Number Of Client(s): " + e.features[0].properties.numberOfClient + '<br>' +
    "Māori: " + (e.features[0].properties.maoriEthnicityPercent * 100).toFixed(2) + '%' + '<br>' +
        "European: " + e.features[0].properties.europeanEthnicityPercent  + '<br>' +
        "Asian: " + e.features[0].properties.asianEthnicityPercent  + '<br>' +
        "Pacific: " + e.features[0].properties.pacificEthnicityPercent  + '<br>' +
        "Others: " + e.features[0].properties.othersEthnicityPercent)
        .addTo(map);
    });
}

async function datasetConfig(dataset, map, firstLoad, serviceType, asaEthnicity, asaSchool, reload, database, activeClientsOnly, entryDateFrom, entryDateTo, exitDateFrom, exitDateTo, selectedGender, selectedAgeRange, selectedIwi, selectedWellbeing) {
    if (firstLoad) {
        createClickEvents(map);
    }

    switch (dataset) {        
        case 'ASA':
            if (!map.getSource('geo-data-asa') || asaEthnicity || asaSchool || selectedGender || selectedAgeRange || selectedIwi || reload) {
                if (map.getSource('geo-data-asa')) {
                    map.removeSource("geo-data-asa");
                }
                const geodata = await api.map.getASAGeoData(asaEthnicity, asaSchool, selectedGender, selectedAgeRange, selectedIwi);
                map.addSource("geo-data-asa", {
                    type: "geojson",
                    data: geodata.data,
                });
            }
            layers.addASADataLayers(map);
            layers.addSA2FilterLayers(map, 'geo-data-asa');
            return { hasData: true };
        case 'Purapura Whetu': {
            const geodata = await api.map.getPPWGeoData(serviceType, database, activeClientsOnly, entryDateFrom, entryDateTo, exitDateFrom, exitDateTo, selectedGender, selectedAgeRange, selectedIwi, asaEthnicity, selectedWellbeing);
            
            if (!geodata || !geodata.data) {
                throw new Error('No data received from PPW API');
            }
            
            // Check if the data has any features (actual results)
            const hasData = geodata.data.features && geodata.data.features.length > 0;
            
            if (!hasData) {
                geodata.data = {
                    type: 'FeatureCollection',
                    features: []
                };
            }
            
            if (!map.getSource('geo-data-ppw')) {
                map.addSource("geo-data-ppw", {
                    type: "geojson",
                    data: geodata.data,
                });
            }
            
            layers.addPPWDataLayers(map);
            layers.addSA2FilterLayers(map, 'geo-data-ppw');
            
            return { hasData };
        }
        case 'Pae Ora Waitaha': {
            if (!map.getSource('geo-data-pae-ora') || reload) {
                if (map.getSource('geo-data-pae-ora')) {
                    map.removeSource('geo-data-pae-ora');
                }
                const geodata = await api.map.getPaeOraGeoData();
                map.addSource('geo-data-pae-ora', {
                    type: 'geojson',
                    data: geodata.data,
                });       
            }
            layers.addPaeOraDataLayers(map);
            layers.addSA2FilterLayers(map, 'geo-data-pae-ora');
            return { hasData: true };
        }
    }
}

export default {
    setupMap,
    datasetConfig
}
